import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Formik } from 'formik';
import { Column, FormInput, useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { isPresent } from 'Shared/utils';
import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  CurrentCarrierChallengeFieldsType,
  CurrentCarrierChallengeFormValuesType,
  transformChallengeCurrentCarrierRequest,
} from 'QuoteApp/services/endpoints/PostChallengeCurrentCarrier';
import { useAppContext } from 'QuoteApp/services/providers';
import {
  generateFieldDataTestId,
  getDriverDataByFieldValue,
  getErrorMessage,
} from 'QuoteApp/utils';
import { useEdit } from 'QuoteApp/utils/contexts';
import {
  getReportMessages,
  ReportChangeMessageType,
  ReportMessages,
} from 'QuoteApp/screens/Reports';
import { ChallengeForm } from '../../ChallengeForm';
import { NoResults } from '../../NoResults';
import { ReportDetailHeaderRow } from '../../ReportDetailHeaderRow';
import { ReportDetailRow, getDisplayValue } from '../../ReportDetailRow';
import styles from './styles.module.css';
import { DriverReportType } from '../../types';

export const CurrentCarrierReport: DriverReportType = ({ driverId }) => {
  const {
    details,
    config,
    appId,
    setDetails,
    rate,
    fetchRate,
    refreshConfig,
    refreshDetails,
  } = useAppContext();
  const { editingId, changeEditingId } = useEdit();
  const { toast } = useToast();
  const identifier = `${driverId}-currentInsurance`;
  const [messages, setMessages] = useState<Array<ReportChangeMessageType>>([]);

  const [, challengeCurrentCarrierData] = useLazyQuoteApi<
    GetDetailsResponseType
  >({
    method: 'POST',
    url: `/insurance_applications/${appId}/insurable_parties/${driverId}/challenge_current_carrier`,
  });

  const driverData = useMemo(
    () =>
      getDriverDataByFieldValue(details.drivers, 'value')[
        details.principalInsuredId
      ],
    [details]
  );

  const challengeCommand = details.commands.drivers[
    driverId
  ].challenging.currentInsurance?.find(
    c => c.name === 'challenge_current_carrier'
  );

  const thirdPartyReported =
    details.drivers[driverId].isCurrentlyInsured.source !== 'user_reported';

  if (!config) return null;

  return (
    <>
      <Formik<CurrentCarrierChallengeFormValuesType>
        enableReinitialize
        initialValues={{
          [identifier]: {
            isCurrentlyInsured: driverData.isCurrentlyInsured,
            currentInsuranceCarrierName: driverData.currentInsuranceCarrierName,
            currentInsuranceInsuredDuration:
              driverData.currentInsuranceInsuredDuration,
            currentInsuranceBiLevel: driverData.currentInsuranceBiLevel,
            currentInsuranceLapseDuration:
              driverData.currentInsuranceLapseDuration,
            currentInsuranceLapseReason: driverData.currentInsuranceLapseReason,
            reasonText: '',
          },
        }}
        onSubmit={async values => {
          try {
            const response = await challengeCurrentCarrierData({
              data: values[identifier],
              transformRequest: transformChallengeCurrentCarrierRequest,
              transformResponse: transformAppDetails,
            });
            if (response.data.status === 'open') {
              const newRate = await fetchRate();
              rate &&
                newRate &&
                setMessages(
                  getReportMessages(
                    rate,
                    newRate,
                    'disputed report data',
                    'current-carrier-challenging'
                  )
                );
            }
            changeEditingId(null);
            setDetails(response.data);
            if (response.data.productToggled) {
              refreshConfig();
            }
          } catch (e) {
            if (e.productToggled) {
              refreshConfig();
              refreshDetails();
            } else {
              toast({
                type: 'error',
                message: getErrorMessage(e),
                withSecondaryNav: false,
                icon: 'error',
              });
            }
          }
        }}
      >
        {({ values, setFieldValue, handleBlur }) => {
          const getInputProps = (
            fieldName: keyof CurrentCarrierChallengeFieldsType
          ) => {
            const fullFieldName = `${identifier}.${fieldName}`;
            return {
              name: fullFieldName,
              className: styles.formInput,
              'data-testid': generateFieldDataTestId(fieldName),
              variant: 'selectAutocomplete' as const,
              onChange: (value: string) => {
                setFieldValue(fullFieldName, value);
              },
              onBlur: handleBlur,
              value: values[identifier][fieldName],
            };
          };

          // When currently insured or no-hit
          if (
            values[identifier].isCurrentlyInsured === 'true' ||
            !thirdPartyReported
          ) {
            return (
              <div data-testid="currently-insured-report-details">
                <ReportDetailHeaderRow
                  canChallenge={isPresent(challengeCommand)}
                >
                  <Column variant="quarter">Currently Insured?</Column>
                  <Column variant="quarter">Current Carrier</Column>
                  <Column variant="quarter">Duration</Column>
                  <Column variant="quarter">BI Limits</Column>
                </ReportDetailHeaderRow>
                {thirdPartyReported && (
                  <ul>
                    <ReportDetailRow
                      identifier={identifier}
                      canChallenge={isPresent(challengeCommand)}
                      challengeCommand={challengeCommand}
                      source={
                        details.drivers[driverId].isCurrentlyInsured.source
                      }
                      challengeForm={
                        <ChallengeForm
                          variant="edit"
                          identifier={identifier}
                          type="current-carrier"
                        />
                      }
                      type="current-carrier"
                    >
                      <Column
                        variant="quarter"
                        className={classNames('fs-mask', {
                          [styles.editing]: editingId === identifier,
                        })}
                      >
                        {editingId === identifier ? (
                          <FormInput
                            {...getInputProps('isCurrentlyInsured')}
                            label="Currently Insured?"
                            options={{ true: 'Yes', false: 'No' }}
                          />
                        ) : (
                          getDisplayValue(values[identifier].isCurrentlyInsured)
                        )}
                      </Column>
                      <Column
                        variant="quarter"
                        className={classNames(
                          'fs-mask',
                          styles.extendOptionWidth,
                          {
                            [styles.editing]: editingId === identifier,
                          }
                        )}
                      >
                        {editingId === identifier ? (
                          <FormInput
                            {...getInputProps('currentInsuranceCarrierName')}
                            label="Current insurance carrier"
                            variant="combobox"
                            options={
                              config.drivers.currentInsurance.carrierName
                            }
                          />
                        ) : (
                          getDisplayValue(
                            config.drivers.currentInsurance.carrierName[
                              values[identifier].currentInsuranceCarrierName
                            ] || values[identifier].currentInsuranceCarrierName
                          )
                        )}
                      </Column>
                      <Column
                        variant="quarter"
                        className={classNames(
                          'fs-mask',
                          styles.extendOptionWidth,
                          {
                            [styles.editing]: editingId === identifier,
                          }
                        )}
                      >
                        {editingId === identifier ? (
                          <FormInput
                            {...getInputProps(
                              'currentInsuranceInsuredDuration'
                            )}
                            label="How long has driver been insured?"
                            options={
                              config.drivers.currentInsurance.insuredDuration
                            }
                          />
                        ) : (
                          getDisplayValue(
                            config.drivers.currentInsurance.insuredDuration[
                              values[identifier].currentInsuranceInsuredDuration
                            ]
                          )
                        )}
                      </Column>
                      <Column
                        variant="quarter"
                        className={classNames(
                          'fs-mask',
                          styles.extendOptionWidth,
                          {
                            [styles.editing]: editingId === identifier,
                          }
                        )}
                      >
                        {editingId === identifier ? (
                          <FormInput
                            {...getInputProps('currentInsuranceBiLevel')}
                            label="Bodily injury limits"
                            options={config.drivers.currentInsurance.biLevel}
                          />
                        ) : (
                          getDisplayValue(
                            config.drivers.currentInsurance.biLevel[
                              values[identifier].currentInsuranceBiLevel
                            ]
                          )
                        )}
                      </Column>
                    </ReportDetailRow>
                  </ul>
                )}
                <ReportMessages messages={messages} />
                <NoResults isVisible={!thirdPartyReported}>
                  No Current Carrier discovered
                </NoResults>
              </div>
            );
          }

          // When not currently insured
          return (
            <div data-testid="not-currently-insured-report-details">
              <ReportDetailHeaderRow canChallenge={isPresent(challengeCommand)}>
                <Column variant="quarter">Currently Insured?</Column>
                <Column variant={editingId === identifier ? 'half' : 'quarter'}>
                  Uninsured Duration
                </Column>
                <Column variant={editingId === identifier ? 'quarter' : 'half'}>
                  Uninsured Reason
                </Column>
              </ReportDetailHeaderRow>
              <ul>
                <ReportDetailRow
                  identifier={identifier}
                  canChallenge={isPresent(challengeCommand)}
                  challengeCommand={challengeCommand}
                  source={details.drivers[driverId].isCurrentlyInsured.source}
                  challengeForm={
                    <ChallengeForm
                      variant="edit"
                      identifier={identifier}
                      type="current-carrier"
                    />
                  }
                  type="current-carrier"
                >
                  <Column
                    variant="quarter"
                    className={classNames('fs-mask', {
                      [styles.editing]: editingId === identifier,
                    })}
                  >
                    {editingId === identifier ? (
                      <FormInput
                        {...getInputProps('isCurrentlyInsured')}
                        label="Currently Insured?"
                        options={{ true: 'Yes', false: 'No' }}
                      />
                    ) : (
                      getDisplayValue(values[identifier].isCurrentlyInsured)
                    )}
                  </Column>
                  <Column
                    variant={editingId === identifier ? 'half' : 'quarter'}
                    className={classNames('fs-mask', styles.extendOptionWidth, {
                      [styles.editing]: editingId === identifier,
                    })}
                  >
                    {editingId === identifier ? (
                      <FormInput
                        {...getInputProps('currentInsuranceLapseDuration')}
                        label="How long has driver been uninsured?"
                        options={config.drivers.currentInsurance.lapseDuration}
                      />
                    ) : (
                      getDisplayValue(
                        config.drivers.currentInsurance.lapseDuration[
                          values[identifier].currentInsuranceLapseDuration
                        ]
                      )
                    )}
                  </Column>
                  <Column
                    variant={editingId === identifier ? 'quarter' : 'half'}
                    className="fs-mask"
                  >
                    {getDisplayValue(
                      config.drivers.currentInsurance.lapseReason[
                        values[identifier].currentInsuranceLapseReason
                      ]
                    )}
                  </Column>
                </ReportDetailRow>
              </ul>
              <ReportMessages messages={messages} />
            </div>
          );
        }}
      </Formik>
    </>
  );
};
